import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseMutationOptions,
  callApi,
  UseQueryResult,
  UseMutationResult,
  OverridableQuery,
} from './index';
import type {
  IErrorApiResponse,
  ISuccessApiResponse,
  IPatchObject,
  ScalarType,
  IValidationError,
} from '@wyz/types';

type PatchVariable = IPatchObject<Record<string, ScalarType>>;
export type CrudOptions<
  TResourceType,
  TCreateFormType = TResourceType,
  TUpdateFormType = TCreateFormType,
  TCreateError = IErrorApiResponse<IValidationError>,
  TUpdateError = TCreateError,
  TPatchError = TCreateError,
  TDeleteError = TCreateError,
> = {
  getOneById?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<TResourceType>, IErrorApiResponse>
  > & {
    args: { id: string };
  };
  create?: UseMutationOptions<
    ISuccessApiResponse<TResourceType>,
    TCreateError,
    TCreateFormType
  >;
  update?: UseMutationOptions<
    ISuccessApiResponse<TResourceType>,
    TUpdateError,
    IPatchObject<Partial<TUpdateFormType>>
  >;
  patch?: UseMutationOptions<
    ISuccessApiResponse<TResourceType>,
    TPatchError,
    PatchVariable
  >;
  remove?: UseMutationOptions<
    ISuccessApiResponse<TResourceType>,
    TDeleteError,
    string
  >;
};
type ApiQueries<
  TResourceType,
  TCreateFormType = TResourceType,
  TUpdateFormType = TCreateFormType,
  TCreateError = IErrorApiResponse<IValidationError>,
  TUpdateError = TCreateError,
  TPatchError = TCreateError,
  TDeleteError = TCreateError,
> = {
  getOneById?: UseQueryResult<
    ISuccessApiResponse<TResourceType>,
    IErrorApiResponse
  >;
  create?: UseMutationResult<
    ISuccessApiResponse<TResourceType>,
    TCreateError,
    TCreateFormType
  >;
  update?: UseMutationResult<
    ISuccessApiResponse<TResourceType>,
    TUpdateError,
    IPatchObject<Partial<TUpdateFormType>>
  >;
  patch?: UseMutationResult<
    ISuccessApiResponse<TResourceType>,
    TPatchError,
    PatchVariable
  >;
  remove?: UseMutationResult<
    ISuccessApiResponse<TResourceType>,
    TDeleteError,
    string
  >;
};

type ApiCaller = ReturnType<typeof callApi>;
type CrudParameters = {
  baseKey: Array<string>;
  endpoint: string;
  caller: ApiCaller;
};
type ApiBaseAction = 'create' | 'update' | 'patch' | 'getOneById' | 'remove';

export const useCrud = <
  TApiActions extends ApiBaseAction = ApiBaseAction,
  TResourceType = unknown,
  TCreateFormType = TResourceType,
  TUpdateFormType = TCreateFormType,
  TCreateError = IErrorApiResponse<IValidationError>,
  TUpdateError = TCreateError,
  TPatchError = TCreateError,
  TDeleteError = TCreateError,
>(
  parameters: CrudParameters,
  options: CrudOptions<
    TResourceType,
    TCreateFormType,
    TUpdateFormType,
    TCreateError,
    TUpdateError,
    TPatchError,
    TDeleteError
  > = {},
): Required<
  Pick<
    ApiQueries<
      TResourceType,
      TCreateFormType,
      TUpdateFormType,
      TCreateError,
      TUpdateError,
      TPatchError,
      TDeleteError
    >,
    TApiActions
  >
> => {
  const createMutation = useMutation({
    mutationFn: (variables) =>
      parameters.caller<ISuccessApiResponse<TResourceType>>(
        parameters.endpoint,
        {
          method: 'POST',
          body: JSON.stringify(variables),
        },
      ),
    keysToInvalidateOnSuccess: [parameters.baseKey],
    ...options.create,
  });

  const updateMutation = useMutation({
    mutationFn: (variables) =>
      parameters.caller<ISuccessApiResponse<TResourceType>>(
        `${parameters.endpoint}/${String(variables.id)}`,
        {
          method: 'PUT',
          body: JSON.stringify(variables.body),
        },
      ),
    keysToInvalidateOnSuccess: [parameters.baseKey],
    ...options.update,
  });

  const patchMutation = useMutation({
    mutationFn: (variables) =>
      parameters.caller<ISuccessApiResponse<TResourceType>>(
        `${parameters.endpoint}/${String(variables.id)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(variables.body),
        },
      ),
    keysToInvalidateOnSuccess: [parameters.baseKey],
    ...options.patch,
  });
  const removeMutation = useMutation({
    mutationFn: (variables) =>
      parameters.caller<ISuccessApiResponse<TResourceType>>(
        `${parameters.endpoint}/${String(variables)}`,
        {
          method: 'DELETE',
        },
      ),
    keysToInvalidateOnSuccess: [parameters.baseKey],
    ...options.remove,
  });

  const getOneQuery = useQuery({
    queryKey: [...parameters.baseKey, options.getOneById?.args.id],
    queryFn: () =>
      parameters.caller<ISuccessApiResponse<TResourceType>>(
        `${parameters.endpoint}/${options.getOneById?.args.id as string}`,
      ),
    gcTime: 0,
    ...options.getOneById,
    enabled:
      Boolean(options.getOneById?.args.id) && options.getOneById?.enabled,
  });
  const api = {
    create: createMutation,
    update: updateMutation,
    patch: patchMutation,
    remove: removeMutation,
    getOneById: getOneQuery,
  } satisfies Required<
    ApiQueries<
      TResourceType,
      TCreateFormType,
      TUpdateFormType,
      TCreateError,
      TUpdateError,
      TPatchError,
      TDeleteError
    >
  >;
  return api as unknown as Required<
    Pick<
      ApiQueries<
        TResourceType,
        TCreateFormType,
        TUpdateFormType,
        TCreateError,
        TUpdateError,
        TPatchError,
        TDeleteError
      >,
      TApiActions
    >
  >;
};
